import { useMemo, useState } from "react";

import { useJobsMonitorPageState } from "@/pages/AdminV2/useJobsMonitorPageState";
import type { AdminV2Job, AdminV2JobStatus } from "@/types/adminV2";
import {
  VNextButton,
  VNextCard,
  VNextNotice,
  VNextPill,
  VNextSelect,
  VNextStat,
} from "@/vnext/primitives";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";
import {
  formatVNextDateTime,
  formatVNextLabel,
} from "@/vnext/shared/formatters";

const STATUS_OPTIONS: Array<AdminV2JobStatus | "all"> = [
  "all",
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
];

const resolveJobCorrelationId = (job: AdminV2Job): string | null =>
  job.failureSummary.correlationId ??
  job.resultSummary.correlationId ??
  job.payloadSummary.correlationId;

const resolveJobRequestId = (job: AdminV2Job): string | null =>
  job.failureSummary.requestId ??
  job.resultSummary.requestId ??
  job.payloadSummary.requestId;

const hasRecordedFailure = (job: AdminV2Job): boolean =>
  job.failureSummary.hasRecordedFailure || job.status === "failed";

const summarizeFailureForTable = (job: AdminV2Job): string => {
  if (!hasRecordedFailure(job)) {
    return "No error";
  }

  return resolveJobCorrelationId(job)
    ? "Failed - use correlation ID"
    : "Failed - check job details";
};

const summarizeFailureForDetail = (job: AdminV2Job): string => {
  if (!hasRecordedFailure(job)) {
    return "No error recorded.";
  }

  return job.failureSummary.message;
};

type JobSafeDataSummary = AdminV2Job["payloadSummary"];

const formatSummaryCount = (value: number | null): string =>
  value === null ? "Not applicable" : String(value);

const formatSummaryType = (
  valueType: JobSafeDataSummary["valueType"],
): string => formatVNextLabel(valueType);

const UNSAFE_SUMMARY_FIELD_VALUE_PATTERN =
  /auth|authorization|bearer|body|cookie|customer|email|export|file|html|jwt|oauth|password|payload|phone|private|provider|raw|secret|select|sql|stack|token|trace|url|uri/i;
const JSON_WEB_TOKEN_PATTERN =
  /^[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}$/;

const isSafeSummaryFieldValue = (value: string): boolean => {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0 || trimmedValue.length > 80) {
    return false;
  }

  return (
    /^[a-z0-9_.:-]+$/i.test(trimmedValue) &&
    !UNSAFE_SUMMARY_FIELD_VALUE_PATTERN.test(trimmedValue) &&
    !JSON_WEB_TOKEN_PATTERN.test(trimmedValue)
  );
};

const renderSafeSummary = (title: string, summary: JobSafeDataSummary) => {
  const displayableSafeFields = summary.safeFields.filter((field) =>
    isSafeSummaryFieldValue(field.value),
  );

  return (
    <div className="vnext-field">
      <span className="vnext-field__label">{title}</span>
      <p className="vnext-muted">{summary.message}</p>
      <div className="vnext-detail-grid">
        <div className="vnext-field">
          <span className="vnext-field__label">Data type</span>
          <strong>{formatSummaryType(summary.valueType)}</strong>
        </div>
        <div className="vnext-field">
          <span className="vnext-field__label">Fields</span>
          <strong>{formatSummaryCount(summary.fieldCount)}</strong>
        </div>
        <div className="vnext-field">
          <span className="vnext-field__label">Items</span>
          <strong>{formatSummaryCount(summary.itemCount)}</strong>
        </div>
        <div className="vnext-field">
          <span className="vnext-field__label">Values</span>
          <strong>{summary.scalarCount}</strong>
        </div>
      </div>
      {displayableSafeFields.length > 0 ? (
        <ul className="vnext-list">
          {displayableSafeFields.map((field) => (
            <li key={field.key}>
              <strong>{`${field.label} (${field.value})`}</strong>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default function JobsRoute() {
  const state = useJobsMonitorPageState();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const summary = useMemo(() => {
    return state.jobs.reduce(
      (current, job) => {
        current[job.status] += 1;
        return current;
      },
      {
        cancelled: 0,
        failed: 0,
        queued: 0,
        running: 0,
        succeeded: 0,
      },
    );
  }, [state.jobs]);
  const emptyStateMessage = state.hasActiveFilters
    ? "No jobs matched these filters. Clear the filters or refresh the queue."
    : "No jobs have been recorded yet. Background work will appear here after it runs.";
  const activeJobId =
    selectedJobId !== null && state.jobs.some((job) => job.id === selectedJobId)
      ? selectedJobId
      : state.jobs.length > 0
        ? state.jobs[0].id
        : null;
  const selectedJob = useMemo(
    () =>
      activeJobId === null
        ? null
        : (state.jobs.find((job) => job.id === activeJobId) ?? null),
    [activeJobId, state.jobs],
  );

  return (
    <SettingsWorkspaceChrome
      subtitle="Monitor background queue throughput and failure states."
      title="Jobs monitor"
    >
      <div className="vnext-stack">
        {state.dashboardSource ? (
          <VNextNotice tone="info">
            Showing {formatVNextLabel(state.statusFilter)} jobs context from
            dashboard
            {` - ${state.dashboardSource}`}
            {state.dashboardWindowHours
              ? ` (last ${state.dashboardWindowHours} hours)`
              : ""}
            {state.jobTypeFilter.trim()
              ? ` filtered by ${state.jobTypeFilter.trim()}`
              : ""}
            .
          </VNextNotice>
        ) : null}
        <div className="vnext-grid vnext-grid--two">
          <VNextCard
            actions={
              <VNextButton
                disabled={!state.hasActiveFilters}
                onClick={state.onClearFilters}
                tone="secondary"
              >
                Clear filters
              </VNextButton>
            }
            title="Filters"
          >
            <div className="vnext-grid">
              <label className="vnext-field">
                <span className="vnext-field__label">Status</span>
                <VNextSelect
                  onChange={(event) =>
                    state.onStatusFilterChange(
                      event.target.value as AdminV2JobStatus | "all",
                    )
                  }
                  value={state.statusFilter}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {formatVNextLabel(option)}
                    </option>
                  ))}
                </VNextSelect>
              </label>
              <label className="vnext-field">
                <span className="vnext-field__label">Job type</span>
                <input
                  className="vnext-input"
                  onChange={(event) =>
                    state.onJobTypeFilterChange(event.target.value)
                  }
                  placeholder="e.g. stripe.webhook"
                  value={state.jobTypeFilter}
                />
              </label>
            </div>
          </VNextCard>

          <VNextCard title="Status summary">
            <div className="vnext-stats-grid">
              <VNextStat label="Queued" value={summary.queued} />
              <VNextStat label="Running" value={summary.running} />
              <VNextStat label="Succeeded" value={summary.succeeded} />
              <VNextStat label="Failed" value={summary.failed} />
              <VNextStat label="Cancelled" value={summary.cancelled} />
            </div>
          </VNextCard>
        </div>

        <VNextCard
          actions={
            <VNextButton onClick={() => void state.refetch()} tone="secondary">
              Refresh jobs
            </VNextButton>
          }
          subtitle="Select a row to inspect safe failure context and queue summaries."
          title="Jobs"
        >
          {state.isLoading ? (
            <div className="vnext-empty">Loading jobs…</div>
          ) : state.isError ? (
            <div className="vnext-empty">Jobs could not be loaded.</div>
          ) : state.jobs.length === 0 ? (
            <div className="vnext-empty">{emptyStateMessage}</div>
          ) : (
            <div className="vnext-scroll-panel">
              <table className="vnext-table">
                <thead>
                  <tr>
                    <th>Job type</th>
                    <th>Status</th>
                    <th>Attempts</th>
                    <th>Failure summary</th>
                    <th>Run at</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {state.jobs.map((job) => (
                    <tr
                      className={
                        job.id === activeJobId
                          ? "vnext-table__row--selected"
                          : undefined
                      }
                      key={job.id}
                      onClick={() => setSelectedJobId(job.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedJobId(job.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <td>{job.jobType}</td>
                      <td>
                        <VNextPill
                          tone={
                            job.status === "failed"
                              ? "danger"
                              : job.status === "succeeded"
                                ? "success"
                                : "accent"
                          }
                        >
                          {formatVNextLabel(job.status)}
                        </VNextPill>
                      </td>
                      <td>
                        {job.attempts}/{job.maxAttempts}
                      </td>
                      <td>{summarizeFailureForTable(job)}</td>
                      <td>{formatVNextDateTime(job.runAt)}</td>
                      <td>{formatVNextDateTime(job.completedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VNextCard>

        {selectedJob !== null ? (
          <VNextCard title="Selected job detail">
            <div className="vnext-detail-grid">
              <div className="vnext-field">
                <span className="vnext-field__label">Job ID</span>
                <strong>{selectedJob.id}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Job type</span>
                <strong>{selectedJob.jobType}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Status</span>
                <strong>{formatVNextLabel(selectedJob.status)}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Tenant</span>
                <strong>{selectedJob.organisationId ?? "Global"}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Attempts</span>
                <strong>
                  {selectedJob.attempts}/{selectedJob.maxAttempts}
                </strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Correlation</span>
                <strong>
                  {resolveJobCorrelationId(selectedJob) ??
                    "No correlation ID recorded"}
                </strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Request ID</span>
                <strong>
                  {resolveJobRequestId(selectedJob) ?? "No request ID recorded"}
                </strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Queue owner</span>
                <strong>{selectedJob.lockedBy ?? "Unclaimed"}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Run at</span>
                <strong>{formatVNextDateTime(selectedJob.runAt)}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Completed</span>
                <strong>{formatVNextDateTime(selectedJob.completedAt)}</strong>
              </div>
            </div>

            <div className="vnext-detail-stack">
              <div className="vnext-field">
                <span className="vnext-field__label">Failure summary</span>
                <p className="vnext-muted">
                  {summarizeFailureForDetail(selectedJob)}
                </p>
              </div>
              {renderSafeSummary(
                "Stored request summary",
                selectedJob.payloadSummary,
              )}
              {renderSafeSummary(
                "Run output summary",
                selectedJob.resultSummary,
              )}
            </div>
          </VNextCard>
        ) : null}
      </div>
    </SettingsWorkspaceChrome>
  );
}
